/**
 * Cross-Browser Scrollbar Utilities
 * This file provides JavaScript helpers for browsers that don't support CSS properties
 * like scrollbar-width and line-clamp
 */

/**
 * Applies no-scrollbar class to elements that need hidden scrollbars
 * Especially helpful for Firefox and other browsers with limited CSS support
 */
export const applyScrollbarUtilities = () => {
  // Find all elements that should have hidden scrollbars
  const elementsToHideScrollbars = document.querySelectorAll('.no-scrollbar, .category-filter');
  
  // Apply classes and styles to ensure cross-browser compatibility
  elementsToHideScrollbars.forEach(element => {
    // Add general class
    element.classList.add('no-scrollbar');
    
    // For older Firefox versions
    if (navigator.userAgent.indexOf('Firefox') !== -1) {
      element.style.overflow = 'hidden';
      element.style.overflowX = 'auto';
      // After slight delay, reset overflow-x to ensure scrolling still works
      setTimeout(() => {
        element.style.overflow = '';
        element.style.overflowX = 'auto';
      }, 10);
    }
  });
};

/**
 * Applies custom scrollbar styles for browsers that don't support CSS scrollbar styling
 */
export const applyCustomScrollbars = () => {
  const customScrollbarElements = document.querySelectorAll('.custom-scrollbar');
  
  customScrollbarElements.forEach(element => {
    // Apply styles based on browser and theme
    // Basic styling for WebKit browsers is handled in CSS
    // This function can be extended for other browsers as needed
  });
};

/**
 * Polyfill for line-clamp
 * Manually truncates text for browsers that don't support -webkit-line-clamp
 */
export const applyLineClampFallback = () => {
  // Only run this for browsers that don't support line-clamp
  const supportsLineClamp = CSS.supports('(-webkit-line-clamp: 1)') || CSS.supports('line-clamp: 1');
  
  if (!supportsLineClamp) {
    const elementsToClamp = document.querySelectorAll('.project-description, [class*="line-clamp-"]');
    
    elementsToClamp.forEach(element => {
      // Get line clamp value from class name or default to 3
      let lines = 3;
      if (element.className.includes('line-clamp-')) {
        const match = element.className.match(/line-clamp-(\d+)/);
        if (match && match[1]) {
          lines = parseInt(match[1], 10);
        }
      }
      
      // Get computed line height and set max height
      const style = window.getComputedStyle(element);
      const lineHeight = parseFloat(style.lineHeight);
      element.style.maxHeight = `${lineHeight * lines}px`;
      element.style.overflow = 'hidden';
      
      // Add ellipsis at the end if needed
      if (element.scrollHeight > element.clientHeight) {
        // Text is being truncated, add ellipsis
        element.classList.add('line-clamp-fallback');
      }
    });
  }
};

// Function to initialize all cross-browser compatibility utilities
export const initCrossBrowserCompatibility = () => {
  applyScrollbarUtilities();
  applyCustomScrollbars();
  applyLineClampFallback();
  
  // Also apply when theme changes
  document.addEventListener('themeChange', () => {
    applyCustomScrollbars();
  });
  
  // Apply on window resize for responsive handling
  window.addEventListener('resize', () => {
    applyLineClampFallback();
  });
};

// Export default for easy importing
export default initCrossBrowserCompatibility;
